import { t } from '@lingui/core/macro';
import { useContext, useEffect, useRef, useState } from 'react';
import { ActionInfo, dataManager, TypeId, type AnyEntity } from '@sdk';
import { useAgentContext } from '@src/components/agent-layout/agent-layout';
import { GitShareFrame } from '@src/components/assets/editor/GitShareBar';
import { ContentEpochContext } from '@src/contexts/content-epoch-context';
import PersistentIframe, { reloadOnNewEpoch, type PersistentIframeHandle } from '@src/components/persistent-iframe';
import { useFS } from '@src/hooks/useFS';
import { openShareLink, previewShareUrl } from './share-links';

/**
 * Render a local HTML file — a chart, a report, a small static site — in an
 * iframe pointed at the file's own served url (`fs/serve`).
 *
 * **The url is the feature.** This pane used to read the file and hand the
 * markup to the frame as `srcDoc`. A `srcdoc` document has no url of its own,
 * so the browser resolved its relative references against the PARENT document,
 * which is the app's dock route: `<a href="page2.html">` became
 * `/dock/shell/page2.html`, a path holding no file — a blank pane locally, and
 * the cookie-gate's Forbidden page on a gated instance. The same missing
 * address broke sibling images, stylesheets, `#anchors`, `srcset`, css
 * `url(...)`, script-driven navigation and nested frames: one cause wearing a
 * dozen costumes, each of which had to be patched separately.
 *
 * Serving ends the whole class at once, because `fs/serve`'s url ends in the
 * file's own path — so `page2.html` beside `index.html` resolves to that file
 * and nothing has to be intercepted, rewritten or inlined. The click
 * interceptor, the `previewLinkTarget` resolver and the data:-uri asset
 * inliner (with its 2MB/8MB ceilings) are gone with it.
 *
 * **`allow-same-origin` is required, and withholding it does not work.** It was
 * withheld at first, on the reasoning that an opaque origin costs only
 * `localStorage` while keeping an agent-written page away from the API and from
 * `parent.document`. On a cookie-gated instance that reasoning is wrong, and it
 * fails in a way local testing cannot see (a desktop install is never gated, so
 * no cookie is ever needed there).
 *
 * A sandboxed frame WITHOUT `allow-same-origin` has an opaque origin, and an
 * opaque origin's *site for cookies* is null — so every request the document
 * itself makes counts as cross-site, and the `__Host-grant` cookie (`SameSite=None;
 * Partitioned`, keyed to the top-level site) is withheld from all of them. Same host or not: the host is not what
 * the browser is deciding on. The symptom is precise and was measured on e2b —
 * the frame's FIRST load is initiated by the parent, so it carries the cookie
 * and the page renders; then its own image fetch and its own link clicks arrive
 * cookie-less and the gate answers each with its Forbidden page.
 *
 * So the trade is not "isolation vs `localStorage`" but "isolation vs the page
 * loading at all". `PersistentIframe` (every `flow app serve` app) has run with
 * `allow-same-origin` on the same host through the same gate all along; this
 * matches it rather than inventing a weaker posture.
 *
 * What that costs, stated plainly: with `allow-scripts` and `allow-same-origin`
 * together the sandbox is not a boundary — the page can reach the API with the
 * user's session and touch `parent.document`. Real isolation needs a SEPARATE,
 * un-gated origin to serve from, which is its own piece of work.
 *
 * **One frame per page per session, parked, never reloaded by a switch.** The page is a
 * `PersistentIframe`: the registry keeps a frame per `src` and only hides it when the
 * session is left, so coming back shows the page as it was — its scroll, its form, its
 * script state — with no request. The `src` names the file and the session (`process`),
 * never the revision: a revision in the url would park a new frame for every edit and keep
 * the old ones forever. An edit reloads the SAME frame instead (`cacheKey`), also when it
 * landed while the page was parked. The vibe host has no per-file write event, only its content
 * epoch (an agent turn that wrote, a re-show), so that reloads the frame too: a remount alone
 * hands the parked document back unchanged.
 *
 * The frame is an app's MECHANISM, not an app's powers: it keeps the page's own short
 * sandbox and asks for no browser features (camera, microphone, clipboard, location).
 */
const PREVIEW_SANDBOX = 'allow-scripts allow-same-origin allow-forms allow-modals allow-popups allow-downloads';

export function HtmlPreview({ path }: { path: string }) {
  const { computeNode, flowId } = useAgentContext();
  const fs = useFS(computeNode?.typeId);

  // The revision the FS store bumps on every write to this path. A new one reloads
  // the frame, which is what makes an agent's edit show up: the response is
  // `no-store`, but the iframe keeps the document it already has until it is
  // navigated again.
  const revision = fs?.revision(path) ?? 0;
  // The process this page is shown beside. The server writes it into the page as
  // `__FLOWPAD_PROCESS_ID__`, so the page's SDK can reach that process
  // (`setDisplayContext`, `enqueue`). The id, not the loaded process: it is in
  // the URL from the first render, so the frame loads once, already bound.
  const processQuery = flowId ? `?process=${encodeURIComponent(flowId)}` : '';
  const src = computeNode?.typeId && fs ? `${fs.getServeUrl(path)}${processQuery}` : null;
  const frameRef = useRef<PersistentIframeHandle>(null);
  const contentEpoch = useContext(ContentEpochContext);
  useEffect(() => {
    if (src) reloadOnNewEpoch(src, contentEpoch, () => frameRef.current?.refresh());
  }, [src, contentEpoch]);
  // The page's share menu asks the host to open WhatsApp, a mail draft or the mail app,
  // none of which the sandboxed frame can open itself. Only this frame's own page, and
  // only the closed list in `previewShareUrl`.
  useEffect(() => {
    const onGuest = (event: MessageEvent) => {
      if (!frameRef.current?.isGuest(event.source)) return;
      const url = previewShareUrl(event.data);
      if (url) openShareLink(url);
    };
    window.addEventListener('message', onGuest);
    return () => window.removeEventListener('message', onGuest);
  }, []);
  // A page received by git is an artifact naming this file: its share bar
  // (version, newer versions, send edits back) sits above the page.
  const [sharedAs, setSharedAs] = useState<AnyEntity | null>(null);
  useEffect(() => {
    let live = true;
    // The backend resolves the path as this machine spells it (a Windows viewer
    // path has no drive letter; the row's has one).
    const action = new ActionInfo('shared-file-artifact-at', null, null, 'POST');
    action.bodyParameters = { path };
    void dataManager
      .callAction<{ path: string }, { type_id: string | null }>(action)
      .then(async (res) => (res?.type_id ? await dataManager.getByTypeId(new TypeId(res.type_id)) : null))
      .then((row) => live && setSharedAs(row ?? null))
      .catch(() => live && setSharedAs(null));
    return () => {
      live = false;
    };
  }, [path]);

  if (!src) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">{t`Loading preview…`}</div>
    );
  }

  return (
    <GitShareFrame entity={sharedAs}>
      {/* White under the frame: a page without a background of its own reads as a page, not the app. */}
      <div className="h-full w-full bg-white">
        <PersistentIframe
          ref={frameRef}
          src={src}
          cacheKey={revision}
          sandbox={PREVIEW_SANDBOX}
          allow=""
          testId="html-preview"
        />
      </div>
    </GitShareFrame>
  );
}

export default HtmlPreview;
